import React from "react";
import Header from "../../components/header";
import MobileHeader from "../../components/MobileHeader";
import { useMediaQuery } from "react-responsive";

export default function PrivacyPolicy({
  siteName = "Axionnest",
  url = "http://axionnest.com/",
}) {
  const isMobile = useMediaQuery({ maxWidth: 768 });

  return (
    <>
      {isMobile ? (
        <MobileHeader hideFilter={true} hideHome={true} hideHamburger={true} />
      ) : (
        <Header hideFilter={true} hideHome={true} />
      )}

      <main
        className="
          max-w-4xl
          mx-auto
          my-8
          p-6
          sm:my-6
          sm:p-4
          font-sans
          text-gray-700
          bg-white
          leading-relaxed
          rounded-xl
          shadow-sm
        "
        aria-labelledby="pp-heading"
      >
        {/* Header */}
        <header className="border-b border-gray-200 pb-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-[#e8f0f7] rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div>
              <h1
                id="pp-heading"
                className="text-2xl sm:text-xl font-semibold text-gray-900 mb-1"
              >
                Privacy Policy
              </h1>
              <p className="text-sm text-gray-500">
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </div>
          </div>

          <p className="text-gray-700">
            At <strong className="text-gray-900">{siteName}</strong> ("we", "our", "us"), we respect your
            privacy. This Privacy Policy explains how we collect, use, and
            protect your information when you use{" "}
            <a
              href={`https://${url}`}
              className="text-[#0f6abf] hover:underline font-medium"
            >
              {url}
            </a>{" "}
            (the "Site").
          </p>
        </header>

        {/* TOC */}
        <nav
          className="
            my-6
            p-5
            bg-gray-50
            border
            border-gray-200
            rounded-xl
          "
          aria-label="Table of contents"
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
            <strong className="text-gray-900">On this page</strong>
          </div>
          <ul className="mt-3 space-y-2">
            <li><a href="#info-we-collect" className="text-[#0f6abf] hover:underline text-sm flex items-center gap-2"><span className="text-gray-500">1.</span> Information We Collect</a></li>
            <li><a href="#how-we-use" className="text-[#0f6abf] hover:underline text-sm flex items-center gap-2"><span className="text-gray-500">2.</span> How We Use Your Information</a></li>
            <li><a href="#cookies" className="text-[#0f6abf] hover:underline text-sm flex items-center gap-2"><span className="text-gray-500">3.</span> Cookies & Tracking</a></li>
            <li><a href="#third-party" className="text-[#0f6abf] hover:underline text-sm flex items-center gap-2"><span className="text-gray-500">4.</span> Third-Party Sharing</a></li>
            <li><a href="#security" className="text-[#0f6abf] hover:underline text-sm flex items-center gap-2"><span className="text-gray-500">5.</span> Data Security</a></li>
            <li><a href="#rights" className="text-[#0f6abf] hover:underline text-sm flex items-center gap-2"><span className="text-gray-500">6.</span> Your Rights</a></li>
            <li><a href="#changes" className="text-[#0f6abf] hover:underline text-sm flex items-center gap-2"><span className="text-gray-500">7.</span> Changes to This Policy</a></li>
            <li><a href="#contact" className="text-[#0f6abf] hover:underline text-sm flex items-center gap-2"><span className="text-gray-500">8.</span> Contact Us</a></li>
          </ul>
        </nav>

        {/* Sections */}
        <section id="info-we-collect" className="my-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
            <h2 className="text-lg font-semibold text-gray-900">1. Information We Collect</h2>
          </div>
          <ul className="ml-6 space-y-2 list-disc text-gray-700">
            <li>Personal details you provide (name, email, phone number, etc.).</li>
            <li>Account information if you register on the Site.</li>
            <li>Usage data such as IP address, browser type, and pages visited.</li>
          </ul>
        </section>

        <section id="how-we-use" className="my-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
            <h2 className="text-lg font-semibold text-gray-900">2. How We Use Your Information</h2>
          </div>
          <p className="text-gray-700 mb-2">We use your information to:</p>
          <ul className="ml-6 space-y-2 list-disc text-gray-700">
            <li>Provide and improve our services.</li>
            <li>Respond to your inquiries or support requests.</li>
            <li>Send updates or promotional materials (if you opt in).</li>
          </ul>
        </section>

        <section id="cookies" className="my-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
            <h2 className="text-lg font-semibold text-gray-900">3. Cookies & Tracking</h2>
          </div>
          <p className="text-gray-700">
            We use cookies and similar technologies to enhance user experience
            and analyze site usage.
          </p>
        </section>

        <section id="third-party" className="my-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
            <h2 className="text-lg font-semibold text-gray-900">4. Third-Party Sharing</h2>
          </div>
          <p className="text-gray-700">
            We do not sell your personal information but may share it with trusted
            service providers or as required by law.
          </p>
        </section>

        <section id="security" className="my-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
            <h2 className="text-lg font-semibold text-gray-900">5. Data Security</h2>
          </div>
          <p className="text-gray-700">
            We take reasonable measures to protect your data, but no method is
            100% secure.
          </p>
        </section>

        <section id="rights" className="my-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
            <h2 className="text-lg font-semibold text-gray-900">6. Your Rights</h2>
          </div>
          <p className="text-gray-700">
            You may access, update, or delete your personal data by contacting us.
          </p>
        </section>

        <section id="changes" className="my-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
            <h2 className="text-lg font-semibold text-gray-900">7. Changes to This Policy</h2>
          </div>
          <p className="text-gray-700">
            Updates to this policy are effective once posted on this page.
          </p>
        </section>

        <section id="contact" className="my-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
            <h2 className="text-lg font-semibold text-gray-900">8. Contact Us</h2>
          </div>
          <div className="flex items-center gap-2 text-gray-700">
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89-4.26a2 2 0 012.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <a
              href="mailto:privacy@yourwebsite.com"
              className="text-[#0f6abf] hover:underline font-medium"
            >
              privacy@yourwebsite.com
            </a>
          </div>
        </section>

        {/* Footer */}
        <footer className="mt-8 pt-6 border-t border-gray-200 text-gray-500 text-sm flex items-center justify-between">
          <span>© {new Date().getFullYear()} {siteName}. All rights reserved.</span>
          <div className="text-xs text-gray-400">
            Version 1.0
          </div>
        </footer>
      </main>
    </>
  );
}
